<script lang="ts">
  import { createEventDispatcher } from 'svelte'
  import type {
    Country,
    Inventory,
    Membership,
    MembershipsPayload,
    MembershipByEmailPayload,
    ReadableQuery,
  } from '../../api'
  import { Buttons, Select } from '../../elements'
  import { normalizeInventoryOptions, handleCurrencySign } from '../../helpers'
  import ViewInventoryBenefits from './ViewInventoryBenefits.svelte'
  import ViewCodeValidation from './ViewCodeValidation.svelte'
  import ViewCodeRedemption from './ViewCodeRedemption.svelte'
  import PaymentInfo from './PaymentInfo.svelte'
  import PaymentEdit from './PaymentEdit.svelte'

  let paymentError
  let paymentEdit = false

  function beginEdit() {
    paymentEdit = true
  }

  async function endEdit() {
    paymentEdit = false
  }

  export let activeMembershipQuery: ReadableQuery<MembershipsPayload | MembershipByEmailPayload>
  export let inventories: Inventory[]
  export let membership: Membership
  export let userCountry: Country

  let redeemVip = false
  let isLifetimeVip = false
  let vipCode = ''
  let vipOptionValue = { isVip: true } as Inventory

  const CANADA = 'CA'
  const currency = handleCurrencySign(userCountry)

  const dispatch = createEventDispatcher()
  const today = new Date()
  const addYears = (date, nYear) => {
    date.setFullYear(date.getFullYear() + nYear)
    return date
  }
  const renewal = addYears(new Date(), 1).toLocaleDateString()

  function monthDiff(d1, d2): number {
    let months: number
    months = (d2.getFullYear() - d1.getFullYear()) * 12
    months -= d1.getMonth()
    months += d2.getMonth()
    return months <= 0 ? 0 : months
  }

  let renewalDate: Date
  let monthsUntilRenewal: number
  let upgradeCredit: number

  $: isVip = membership ? inventories.some((i) => i.id === membership.inventoryId && i.isVip) : false
  $: {
    if (membership) {
      renewalDate = new Date(membership.expireAt)
      monthsUntilRenewal = monthDiff(today, renewalDate)
      upgradeCredit = (monthsUntilRenewal / 12) * membership.price
    }
  }

  let selected: Inventory
  $: selected = membership && inventories.filter(({ skuCode }) => membership.skuCode === skuCode)[0]

  function cancelMembership() {
    dispatch('cancel', membership)
  }

  function change(product) {
    dispatch('change', product)
  }

  function showSelectOptions(inventories) {
    const options = normalizeInventoryOptions(inventories, currency)
    if (userCountry !== CANADA) {
      options.push({ value: vipOptionValue, label: 'VIP' })
    }
    return options
  }
</script>

{#if paymentEdit}
  <PaymentEdit on:finish={endEdit} />
{:else if redeemVip}
  <ViewCodeRedemption
    {activeMembershipQuery}
    {isLifetimeVip}
    {vipCode}
    {userCountry}
    on:cancel={() => (redeemVip = false)}
    on:completeVipRedemption
  />
{:else}
  <div class="current membership">
    <div>
      <span class="membership label">Current:</span>
      <span class="membership name" data-cy="current-membership-mame">{membership?.name || 'None'}</span>
    </div>
    {#if membership && !/^process.env.LIFETIME_VIP_SKU/.test(membership.skuCode)}
      <div><button data-test="cancel-plan" class="cancel-plan" on:click={cancelMembership}>Cancel plan</button></div>
    {/if}
  </div>

  <div class="select">
    <Select
      dataCy="select-membership"
      value={selected}
      on:input={({ detail }) => (selected = detail)}
      placeholder="Please choose a plan"
      options={showSelectOptions(inventories)}
    />
  </div>

  {#if selected?.isVip}
    {#if membership}
      {#if isVip}
        <p class="benefits label" data-test="benefits-label">Current Benefits</p>
        <ViewInventoryBenefits {inventories} {userCountry} selectedInventory={selected} />
      {:else}
        <p>You should cancel the current paid plan before redeeming a VIP.</p>
      {/if}
    {:else}
      <ViewCodeValidation
        on:redeem={({ detail: { checked, code } }) => {
          redeemVip = true
          isLifetimeVip = checked
          vipCode = code
        }}
      />
    {/if}
  {:else if isVip}
    You can only change the plan once the current VIP membership is canceled.
  {:else}
    <hr />
    {#if membership && membership.skuCode === selected?.skuCode}
      <p class="benefits label" data-test="benefits-label">Current Benefits</p>
      <ViewInventoryBenefits {inventories} {userCountry} selectedInventory={membership} />
      <PaymentInfo title="Update Payment Method" error={paymentError} on:edit={beginEdit} />
    {:else if selected?.skuCode}
      <p class="benefits label" data-test="benefits-label">New Benefits: {selected.name}</p>
      <ViewInventoryBenefits {inventories} {userCountry} selectedInventory={selected} />
      <p style="margin-top: 32px;">Effective:</p>
      {#if selected?.level > membership?.level}
        <p class="benefits content">
          Account benefits will be upgraded immediately and the renewal date will be extended to {renewal} at the end of
          the current membership term.
        </p>
        <br />
        <p data-cy="subtotal"><strong>Sub Total: {currency}{(selected.priceCents.amount / 100).toFixed(2)}</strong></p>
        <p class="change-upgrade-credit">
          <strong>Credit: {currency}{(upgradeCredit / 100).toFixed(2)}</strong>
          <span>from the {monthsUntilRenewal} months remaining on current plan</span>
        </p>
        <p>
          <strong>Amount Due: {currency}{((selected.priceCents.amount - upgradeCredit) / 100).toFixed(2)}</strong>
        </p>
      {:else if selected?.level < membership?.level}
        <p class="benefits content">
          Account benefits will be downgraded at the end of the current membership term,
          {renewalDate.toLocaleDateString()}.
        </p>
      {:else}
        <p class="benefits content">
          Account benefits will be added immediately and the renewal date will be set to {renewal}.
        </p>
        <br />
        <p data-cy="subtotal"><strong>Sub Total: {currency}{(selected.priceCents.amount / 100).toFixed(2)}</strong></p>
      {/if}
    {:else}
      <PaymentInfo title="Update Payment Method" error={paymentError} on:edit={beginEdit} />
    {/if}
    <Buttons
      confirm="Continue"
      showCancel={false}
      showConfirm={!!(selected?.skuCode && membership?.skuCode !== selected.skuCode)}
      on:confirm={() => change(selected)}
    />
  {/if}
{/if}

<style>
  .select {
    margin-bottom: 24px;
  }
  .membership.label {
    color: #8e949a;
  }

  .membership.name {
    font-weight: 600;
  }

  .membership.current {
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
  }

  .membership.current * {
    line-height: 22px;
  }

  .membership.current .cancel-plan {
    background: none;
    border: none;
    color: #007bb3;
    font-size: 14px;
    padding: 0;
    margin: 0;
  }

  hr {
    height: 1px;
    background-color: #dee0e2;
    border: none;
    margin: 0;
  }

  .benefits.label {
    margin-top: 24px;
    margin-bottom: 8px;
  }

  .benefits.content {
    font-size: 12px;
    line-height: 16px;
    color: #484c51;
  }

  .change-upgrade-credit {
    line-height: 16px;
  }
  .change-upgrade-credit > span {
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
    color: #8e949a;
  }
</style>
